// src/components/pages/PhoneOtpPage.js
import React, { useState, useEffect, useRef } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { styled } from '@mui/material/styles';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Link,
} from '@mui/material';
import { TOP_APP_BAR_HEIGHT } from '../../constants/constants';
import {
  getAuth,
  RecaptchaVerifier,
  PhoneAuthProvider,
  signInWithPhoneNumber,
  linkWithCredential,
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { updateProfileInFirestore } from '../../services/profileService';
import { isValidPhoneNumber } from 'react-phone-number-input';

// 1) Styled wrapper to mimic original MUI TextField look
const StyledPhoneInput = styled(PhoneInput)(({ theme }) => ({
  // 1) Flex container so children (flag select + input) align center
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),

  // 2) Style the text input itself
  '& .PhoneInputInput': {
    flex: 1,                    // fill remaining space
    height: '56px',
    borderRadius: '50px',
    paddingLeft: theme.spacing(1),
    fontSize: '1rem',
    border: `1px solid ${theme.palette.grey[400]}`,
    outline: 'none',
  },

  // 3) Style the country picker
  '& .PhoneInputCountrySelect': {
    display: 'inline-flex',     // so its children can center
    alignItems: 'center',       // vertical centering
    height: '56px',             // match the input height
    paddingLeft: theme.spacing(1),
    background: 'transparent',
    border: 0,
    marginRight: theme.spacing(1),
  },

  // 4) Ensure the flag icon is centered within that selector
  '& .PhoneInputCountryIcon': {
    verticalAlign: 'middle',
  },
}));


const PhoneOtpPage = () => {
  const auth = getAuth();
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const otpInputRef = useRef(null);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [sending, setSending] = useState(false);
  const [verificationId, setVerificationId] = useState(null);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Skip if already verified
  useEffect(() => {
    if (auth.currentUser?.phoneNumber) {
      navigate('/', { replace: true });
    }
  }, [auth.currentUser, navigate]);

  // (Re)initialize invisible reCAPTCHA
  const initRecaptcha = async () => {
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch { }
    }
    window.recaptchaVerifier = new RecaptchaVerifier(
      auth,
      recaptchaRef.current,
      { size: 'invisible' }
    );
    await window.recaptchaVerifier.render();
  };
  useEffect(() => {
    if (recaptchaRef.current) initRecaptcha();
  }, [auth]);

  // Countdown for “Resend”
  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      setCanResend(false);
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);
  const startResendTimer = () => setResendTimer(30);

  const getFriendlyErrorMessage = (err) => {
    switch (err.code) {
      case 'auth/credential-already-in-use':
        return 'This phone number is already in use. Try another number or sign in with the existing account.';
      case 'auth/invalid-verification-code':
        return 'That code is invalid. Please check and try again.';
      case 'auth/code-expired':
        return 'The code has expired. Please request a new one.';
      case 'auth/too-many-requests':
        return 'Too many requests. Please wait a bit and try again.';
      default:
        return err.message;
    }
  };

  // Send OTP
  const handleSendOtp = async () => {
    setError('');
    if (!isValidPhoneNumber(phoneNumber)) {
      setError('Please enter a valid phone number.');
      return;
    }
    setSending(true);
    try {
      const { verificationId } = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        window.recaptchaVerifier
      );
      setVerificationId(verificationId);
      startResendTimer();
      setTimeout(() => otpInputRef.current?.focus(), 300);
    } catch (err) {
      console.error('Error sending code:', err);
      setError(getFriendlyErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    setError('');
    if (!otp || !verificationId) {
      setError('Please enter the code.');
      return;
    }
    setVerifying(true);
    try {
      const credential = PhoneAuthProvider.credential(verificationId, otp);
      const user = auth.currentUser;
      if (!user) throw new Error('No authenticated user.');
      await linkWithCredential(user, credential);
      await updateProfileInFirestore(user.uid, {
        phoneVerified: true,
        phoneNumber: user.phoneNumber,
      });
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Error verifying code:', err);
      setError(getFriendlyErrorMessage(err));
    } finally {
      setVerifying(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setError('');
    if (!isValidPhoneNumber(phoneNumber)) {
      setError('Please enter a valid phone number.');
      return;
    }
    await initRecaptcha();
    setSending(true);
    try {
      const { verificationId } = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        window.recaptchaVerifier
      );
      setVerificationId(verificationId);
      startResendTimer();
      setTimeout(() => otpInputRef.current?.focus(), 300);
    } catch (err) {
      console.error('Error resending code:', err);
      setError(getFriendlyErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  // Let them correct typos
  const handleChangeNumber = () => {
    setVerificationId(null);
    setOtp('');
    setError('');
  };

  if (!auth.currentUser) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: `calc(100vh - ${TOP_APP_BAR_HEIGHT}px)`,
        }}
      >
        <Typography variant="h6">No user is signed in.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 4,
        backgroundColor: 'white',
        height: `calc(100vh - ${TOP_APP_BAR_HEIGHT}px)`,
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
        Verify Your Phone Number
      </Typography>

      {/* invisible reCAPTCHA */}
      <div ref={recaptchaRef} style={{ display: 'none' }} />

      {!verificationId ? (
        <>
          {/* 1: StyledPhoneInput restores your pill shape */}
          <StyledPhoneInput
            id="phone-input"
            defaultCountry="US"
            international
            countryCallingCodeEditable={false}
            placeholder="+1 (___) ___-____"
            value={phoneNumber}
            onChange={setPhoneNumber}
            autoFocus
            type="tel"
            aria-invalid={!!error}
            aria-describedby={error ? 'phone-error-text' : undefined}
          />

          {error && (
            <Typography
              id="phone-error-text"
              color="error"
              sx={{ mb: 2 }}
            >
              {error}
            </Typography>
          )}

          {/* 3: spinner inside the button */}
          <Button
            variant="contained"
            fullWidth
            disabled={
              sending ||
              !phoneNumber ||
              !isValidPhoneNumber(phoneNumber)
            }
            onClick={handleSendOtp}
            sx={(theme) => ({
              backgroundColor: theme.colors.blue,
              color: theme.colors.white,
              borderRadius: '50px',
              padding: '14px 30px',
              mb: 1,
              fontWeight: 'bold',
              fontSize: '1rem',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            })}
          >
            {sending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Send Code'
            )}
          </Button>
        </>
      ) : (
        <>
          <TextField
            label="Enter Code"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            inputRef={otpInputRef}
            fullWidth
            type="tel"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '50px',
                pl: 1,
              },
            }}
            inputProps={{
              'aria-invalid': !!error,
              'aria-describedby': error ? 'otp-error-text' : undefined,
            }}
          />

          <Button
            variant="contained"
            fullWidth
            disabled={verifying}
            onClick={handleVerifyOtp}
            sx={(theme) => ({
              backgroundColor: theme.colors.blue,
              color: theme.colors.white,
              borderRadius: '50px',
              padding: '14px 30px',
              mb: 1,
              fontWeight: 'bold',
              fontSize: '1rem',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            })}
          >
            {verifying ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Verify Code'
            )}
          </Button>

          <Button
            variant="text"
            fullWidth
            disabled={!canResend}
            onClick={handleResendOtp}
            sx={(theme) => ({
              backgroundColor: theme.colors.white,
              color: theme.colors.blue,
              borderRadius: '50px',
              border: `1px solid ${theme.colors.blue}`,
              padding: '12px 30px',
              mb: 1,
              fontWeight: 'bold',
              fontSize: '1rem',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            })}
          >
            {canResend
              ? "Didn't get a code? Resend!"
              : `Resend in ${resendTimer}s`}
          </Button>

          <Link
            component="button"
            variant="body2"
            onClick={handleChangeNumber}
            sx={{ display: 'block', textAlign: 'center', mb: 1 }}
          >
            Change number
          </Link>

          {error && (
            <Typography
              id="otp-error-text"
              color="error"
              sx={{ mt: 1 }}
            >
              {error}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default PhoneOtpPage;
